import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";

// ---- Data Types ----
interface Property {
  id: string;
  name: string;
  projectId: string;
  amId: string;
  monthlyData: MonthData[];
}
interface MonthData {
  month: string;
  incomeBudget: number;
  incomeActual: number;
  expenseBudget: number;
  expenseActual: number;
}
interface Project {
  id: string;
  name: string;
}
interface AM {
  id: string;
  name: string;
}

// ---- Seed Data ----
const projects: Project[] = [
  { id: "pj-1", name: "東京都心ファンドI" },
  { id: "pj-2", name: "関西レジデンスファンド" },
  { id: "pj-3", name: "九州商業施設ファンド" },
  { id: "pj-4", name: "名古屋オフィスファンド" },
  { id: "pj-5", name: "北海道リゾートファンド" },
  { id: "pj-6", name: "横浜ベイエリアファンド" },
  { id: "pj-7", name: "仙台駅前開発ファンドII" },
  { id: "pj-8", name: "京都町家再生ファンド" },
  { id: "pj-9", name: "広島駅南ファンド" },
  { id: "pj-10", name: "沖縄リゾートファンドIII" },
];
const ams: AM[] = [
  { id: "am-1", name: "ABCアセットマネジメント" },
  { id: "am-2", name: "XYZ投資顧問" },
  { id: "am-3", name: "日本プロパティAM" },
];

const months12 = [
  "4月",
  "5月",
  "6月",
  "7月",
  "8月",
  "9月",
  "10月",
  "11月",
  "12月",
  "1月",
  "2月",
  "3月",
];

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// 物件ごとに異なる推移パターンを生成
// pattern: 0=安定型, 1=右肩上がり, 2=右肩下がり, 3=V字回復, 4=山型, 5=季節変動大
function seedMonthly(
  baseBudget: number,
  _variance: number,
  pattern = 0,
): MonthData[] {
  const trendMultipliers: Record<number, number[]> = {
    0: [1.0, 1.02, 0.98, 1.01, 0.99, 1.0, 1.01, 0.98, 1.02, 1.0, 0.99, 1.01], // 安定
    1: [0.4, 0.5, 0.6, 0.72, 0.85, 1.0, 1.15, 1.3, 1.5, 1.7, 1.85, 2.0], // 急上昇
    2: [2.0, 1.8, 1.55, 1.3, 1.1, 0.9, 0.7, 0.55, 0.45, 0.38, 0.32, 0.3], // 急降下
    3: [1.2, 0.8, 0.4, 0.2, 0.15, 0.25, 0.5, 0.8, 1.1, 1.4, 1.6, 1.7], // V字回復
    4: [0.3, 0.5, 0.9, 1.3, 1.6, 1.8, 1.7, 1.4, 1.0, 0.6, 0.35, 0.2], // 山型ピーク
    5: [1.8, 0.4, 1.6, 0.3, 1.9, 0.5, 1.7, 0.35, 1.5, 0.45, 1.8, 0.6], // 激しい変動
    6: [0.5, 0.5, 0.5, 0.5, 0.5, 1.8, 1.8, 1.8, 1.8, 1.8, 0.5, 0.5], // 後半急伸
    7: [1.5, 1.5, 1.5, 0.3, 0.3, 0.3, 0.3, 1.2, 1.5, 1.5, 1.5, 1.5], // 中央谷型
  };
  const trend = trendMultipliers[pattern] ?? trendMultipliers[0];

  return months12.map((month, i) => {
    const budgetBase = Math.round(baseBudget * trend[i]);
    const rndIncome = seededRandom(baseBudget + i * 7 + pattern * 31);
    const rndExpense = seededRandom(baseBudget + i * 13 + pattern * 47);
    const incomeActual =
      i < 9 ? Math.round(budgetBase * (0.85 + rndIncome * 0.3)) : 0;
    const expBudget =
      Math.round(budgetBase * (0.12 + seededRandom(pattern * 11 + i) * 0.08)) +
      (i === 2 || i === 8 ? Math.round(budgetBase * 0.3) : 0);
    const expActual =
      i < 9 ? Math.round(expBudget * (0.8 + rndExpense * 0.4)) : 0;
    return {
      month,
      incomeBudget: budgetBase,
      incomeActual,
      expenseBudget: expBudget,
      expenseActual: expActual,
    };
  });
}

const properties: Property[] = [
  // pj-1: 東京都心ファンド — 安定 / 急上昇 / V字回復（3物件で推移がバラバラ）
  {
    id: "prop-1",
    name: "テスト物件",
    projectId: "pj-1",
    amId: "am-1",
    monthlyData: seedMonthly(900000, 10000, 0),
  },
  {
    id: "prop-2",
    name: "渋谷マンション",
    projectId: "pj-1",
    amId: "am-1",
    monthlyData: seedMonthly(1200000, 15000, 1),
  },
  {
    id: "prop-3",
    name: "新宿オフィスビル",
    projectId: "pj-1",
    amId: "am-2",
    monthlyData: seedMonthly(2500000, 30000, 3),
  },
  // pj-2: 関西レジデンス — 激しい変動 / 後半急伸
  {
    id: "prop-4",
    name: "梅田タワー",
    projectId: "pj-2",
    amId: "am-1",
    monthlyData: seedMonthly(1800000, 20000, 5),
  },
  {
    id: "prop-5",
    name: "難波レジデンス",
    projectId: "pj-2",
    amId: "am-2",
    monthlyData: seedMonthly(950000, 12000, 6),
  },
  // pj-3: 九州商業 — 急降下 / 中央谷型
  {
    id: "prop-6",
    name: "博多商業ビル",
    projectId: "pj-3",
    amId: "am-2",
    monthlyData: seedMonthly(3200000, 50000, 2),
  },
  {
    id: "prop-7",
    name: "天神オフィス",
    projectId: "pj-3",
    amId: "am-3",
    monthlyData: seedMonthly(1400000, 18000, 7),
  },
  // pj-4: 名古屋 — V字回復 / 山型ピーク
  {
    id: "prop-8",
    name: "栄タワーレジデンス",
    projectId: "pj-4",
    amId: "am-1",
    monthlyData: seedMonthly(1600000, 22000, 3),
  },
  {
    id: "prop-9",
    name: "名駅オフィスビル",
    projectId: "pj-4",
    amId: "am-3",
    monthlyData: seedMonthly(2200000, 28000, 4),
  },
  // pj-5: 北海道リゾート — 山型(夏ピーク) / 急上昇
  {
    id: "prop-10",
    name: "札幌リゾートヴィラ",
    projectId: "pj-5",
    amId: "am-2",
    monthlyData: seedMonthly(800000, 25000, 4),
  },
  {
    id: "prop-11",
    name: "ニセココンドミニアム",
    projectId: "pj-5",
    amId: "am-2",
    monthlyData: seedMonthly(1100000, 30000, 1),
  },
  // pj-6: 横浜ベイ — 後半急伸 / 急降下（真逆の推移）
  {
    id: "prop-12",
    name: "みなとみらいオフィス",
    projectId: "pj-6",
    amId: "am-1",
    monthlyData: seedMonthly(2800000, 35000, 6),
  },
  {
    id: "prop-13",
    name: "横浜駅前レジデンス",
    projectId: "pj-6",
    amId: "am-3",
    monthlyData: seedMonthly(1350000, 16000, 2),
  },
  // pj-7: 仙台 — 中央谷型 / 激しい変動
  {
    id: "prop-14",
    name: "仙台駅前ビル",
    projectId: "pj-7",
    amId: "am-3",
    monthlyData: seedMonthly(1700000, 20000, 7),
  },
  {
    id: "prop-15",
    name: "仙台青葉レジデンス",
    projectId: "pj-7",
    amId: "am-1",
    monthlyData: seedMonthly(900000, 11000, 5),
  },
  // pj-8: 京都 — 激しい変動(観光) / V字回復
  {
    id: "prop-16",
    name: "祇園町家ホテル",
    projectId: "pj-8",
    amId: "am-2",
    monthlyData: seedMonthly(600000, 20000, 5),
  },
  {
    id: "prop-17",
    name: "河原町商業施設",
    projectId: "pj-8",
    amId: "am-3",
    monthlyData: seedMonthly(2000000, 25000, 3),
  },
  // pj-9: 広島 — 急降下
  {
    id: "prop-18",
    name: "広島駅南オフィス",
    projectId: "pj-9",
    amId: "am-1",
    monthlyData: seedMonthly(1500000, 18000, 2),
  },
  // pj-10: 沖縄リゾート — 山型 / 後半急伸
  {
    id: "prop-19",
    name: "那覇シーサイドホテル",
    projectId: "pj-10",
    amId: "am-2",
    monthlyData: seedMonthly(1050000, 30000, 4),
  },
  {
    id: "prop-20",
    name: "北谷リゾートヴィラ",
    projectId: "pj-10",
    amId: "am-3",
    monthlyData: seedMonthly(750000, 22000, 6),
  },
];

// ---- Helpers ----
const fmt = (n: number) => (n === 0 ? "-" : `¥${n.toLocaleString("ja-JP")}`);
const fmtShort = (n: number) => {
  if (n === 0) return "-";
  if (Math.abs(n) >= 10000000) return `${(n / 10000000).toFixed(1)}千万`;
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(0)}万`;
  return n.toLocaleString("ja-JP");
};

type GroupBy = "project" | "am" | "custom";
type ChartMetric = "income" | "expense" | "noi";
type CardLayout = "grid" | "list";

const SERIES_STYLES = [
  { stroke: "#0077a3", dash: "" },
  { stroke: "#00a0c8", dash: "8 4" },
  { stroke: "#004d6b", dash: "3 3" },
  { stroke: "#5cc8e0", dash: "12 4 3 4" },
  { stroke: "#007fa8", dash: "6 2" },
];
const BAR_BUDGET_COLOR = "#b8e2ef";
const BAR_ACTUAL_COLOR = "#0077a3";
const COLLAPSED_VISIBLE = 6;

function aggregateMonthly(props: Property[]) {
  return months12.map((_month, i) => {
    const incomeBudget = props.reduce(
      (s, p) => s + p.monthlyData[i].incomeBudget,
      0,
    );
    const incomeActual = props.reduce(
      (s, p) => s + p.monthlyData[i].incomeActual,
      0,
    );
    const expenseBudget = props.reduce(
      (s, p) => s + p.monthlyData[i].expenseBudget,
      0,
    );
    const expenseActual = props.reduce(
      (s, p) => s + p.monthlyData[i].expenseActual,
      0,
    );
    return {
      incomeBudget,
      incomeActual,
      expenseBudget,
      expenseActual,
      noiBudget: incomeBudget - expenseBudget,
      noiActual: incomeActual - expenseActual,
    };
  });
}

function propertyNOI(p: Property) {
  return p.monthlyData
    .slice(0, 6)
    .reduce((s, m) => s + (m.incomeActual - m.expenseActual), 0);
}

// ---- Mini Sparkline (SVG) ----
function MiniSparkline({
  data,
  style,
}: {
  data: number[];
  style: { stroke: string; dash: string };
}) {
  if (!data || data.length === 0) return <svg width={80} height={24} />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const w = 80;
  const h = 24;
  const denominator = data.length > 1 ? data.length - 1 : 1;
  const points = data
    .map(
      (v, i) =>
        `${(i / denominator) * w},${h - ((v - min) / range) * (h - 4) - 2}`,
    )
    .join(" ");
  return (
    <svg width={w} height={h} style={{ flexShrink: 0 }}>
      <polyline
        points={points}
        fill="none"
        stroke={style.stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={style.dash || undefined}
      />
    </svg>
  );
}

// ---- Main Component ----
export function BudgetAnalysisSample() {
  const [fiscalYear, setFiscalYear] = useState("2025");
  const [groupBy, setGroupBy] = useState<GroupBy>("project");
  const [chartMetric, setChartMetric] = useState<ChartMetric>("noi");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [filterProjectId, setFilterProjectId] = useState("all");
  const [filterAmId, setFilterAmId] = useState("all");
  const [cardLayout, setCardLayout] = useState<CardLayout>("grid");
  const [cardsExpanded, setCardsExpanded] = useState(false);
  const [cardSearchQuery, setCardSearchQuery] = useState("");
  const [visibleGroupIds, setVisibleGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [chartInitialized, setChartInitialized] = useState(false);

  const filteredProperties = useMemo(() => {
    return properties.filter((p) => {
      if (filterProjectId !== "all" && p.projectId !== filterProjectId)
        return false;
      if (filterAmId !== "all" && p.amId !== filterAmId) return false;
      return true;
    });
  }, [filterProjectId, filterAmId]);

  const groups = useMemo(() => {
    if (groupBy === "project") {
      return projects.map((pj) => ({
        id: pj.id,
        name: pj.name,
        properties: properties.filter((p) => p.projectId === pj.id),
      }));
    }
    if (groupBy === "am") {
      return ams.map((am) => ({
        id: am.id,
        name: am.name,
        properties: properties.filter((p) => p.amId === am.id),
      }));
    }
    const selected = properties.filter((p) =>
      selectedPropertyIds.includes(p.id),
    );
    return selected.length > 0
      ? [{ id: "custom", name: "選択物件", properties: selected }]
      : [];
  }, [groupBy, selectedPropertyIds]);

  const groupSummaries = useMemo(() => {
    return groups.map((g) => {
      const agg = aggregateMonthly(g.properties);
      const ytdSlice = agg.slice(0, 6);
      const ytdIncomeBudget = ytdSlice.reduce((s, m) => s + m.incomeBudget, 0);
      const ytdIncomeActual = ytdSlice.reduce((s, m) => s + m.incomeActual, 0);
      const ytdExpenseBudget = ytdSlice.reduce(
        (s, m) => s + m.expenseBudget,
        0,
      );
      const ytdExpenseActual = ytdSlice.reduce(
        (s, m) => s + m.expenseActual,
        0,
      );
      return {
        ...g,
        agg,
        ytdIncomeBudget,
        ytdIncomeActual,
        ytdExpenseBudget,
        ytdExpenseActual,
        ytdNOIBudget: ytdIncomeBudget - ytdExpenseBudget,
        ytdNOIActual: ytdIncomeActual - ytdExpenseActual,
        propertyCount: g.properties.length,
      };
    });
  }, [groups]);

  useEffect(() => {
    if (groupSummaries.length > 0 && !chartInitialized) {
      const sorted = [...groupSummaries].sort(
        (a, b) => b.ytdNOIActual - a.ytdNOIActual,
      );
      setVisibleGroupIds(new Set(sorted.slice(0, 3).map((g) => g.id)));
      setChartInitialized(true);
    }
  }, [groupSummaries, chartInitialized]);

  useEffect(() => {
    setCardsExpanded(false);
    setCardSearchQuery("");
    setChartInitialized(false);
  }, [groupBy]);

  const toggleGroupVisibility = (id: string) => {
    setVisibleGroupIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const chartVisibleGroups = useMemo(
    () => groups.filter((g) => visibleGroupIds.has(g.id)),
    [groups, visibleGroupIds],
  );
  const visibleGroupOrder = useMemo(
    () => groups.filter((g) => visibleGroupIds.has(g.id)).map((g) => g.id),
    [groups, visibleGroupIds],
  );
  function getSeriesStyle(groupId: string) {
    const idx = visibleGroupOrder.indexOf(groupId);
    return idx < 0
      ? { stroke: "#9ca3af", dash: "" }
      : SERIES_STYLES[idx % SERIES_STYLES.length];
  }

  const metricLabel =
    chartMetric === "noi" ? "NOI" : chartMetric === "income" ? "収入" : "支出";

  // ---- Chart 1: Portfolio Overview (AreaChart → LineChart with area) ----
  const portfolioChartData = useMemo(() => {
    const agg = aggregateMonthly(properties);
    return {
      budget: agg.map((m) =>
        chartMetric === "noi"
          ? m.noiBudget
          : chartMetric === "income"
            ? m.incomeBudget
            : m.expenseBudget,
      ),
      actual: agg.map((m) =>
        chartMetric === "noi"
          ? m.noiActual
          : chartMetric === "income"
            ? m.incomeActual
            : m.expenseActual,
      ),
    };
  }, [chartMetric]);

  const portfolioTotals = useMemo(() => {
    const agg = aggregateMonthly(properties);
    const ytd = agg.slice(0, 6);
    return {
      incomeBudget: ytd.reduce((s, m) => s + m.incomeBudget, 0),
      incomeActual: ytd.reduce((s, m) => s + m.incomeActual, 0),
      expenseBudget: ytd.reduce((s, m) => s + m.expenseBudget, 0),
      expenseActual: ytd.reduce((s, m) => s + m.expenseActual, 0),
      noiBudget: ytd.reduce((s, m) => s + m.noiBudget, 0),
      noiActual: ytd.reduce((s, m) => s + m.noiActual, 0),
    };
  }, []);

  // ---- Chart 2: Line comparison data ----
  const comparisonSeriesData = useMemo(() => {
    const seriesList: {
      id: string;
      label: string;
      data: number[];
      color: string;
      dashStyle?: string;
    }[] = [];
    chartVisibleGroups.forEach((g) => {
      const agg = aggregateMonthly(g.properties);
      const style = getSeriesStyle(g.id);
      const budgetData = agg.map((m) =>
        chartMetric === "noi"
          ? m.noiBudget
          : chartMetric === "income"
            ? m.incomeBudget
            : m.expenseBudget,
      );
      const actualData = agg.map((m) =>
        chartMetric === "noi"
          ? m.noiActual
          : chartMetric === "income"
            ? m.incomeActual
            : m.expenseActual,
      );
      seriesList.push({
        id: `${g.id}-budget`,
        label: `${g.name} 予算`,
        data: budgetData,
        color: style.stroke,
        dashStyle: "4 4",
      });
      seriesList.push({
        id: `${g.id}-actual`,
        label: `${g.name} 実績`,
        data: actualData,
        color: style.stroke,
      });
    });
    return seriesList;
  }, [chartVisibleGroups, chartMetric]);

  // ---- Chart 3: Horizontal Bar (YTD comparison) ----
  const ytdBarData = useMemo(() => {
    const sorted = [...groupSummaries]
      .filter((g) => visibleGroupIds.has(g.id))
      .sort((a, b) => b.ytdNOIActual - a.ytdNOIActual);
    return sorted.map((g) => ({
      name: g.name.length > 10 ? g.name.slice(0, 10) + "..." : g.name,
      fullName: g.name,
      budget:
        chartMetric === "noi"
          ? g.ytdNOIBudget
          : chartMetric === "income"
            ? g.ytdIncomeBudget
            : g.ytdExpenseBudget,
      actual:
        chartMetric === "noi"
          ? g.ytdNOIActual
          : chartMetric === "income"
            ? g.ytdIncomeActual
            : g.ytdExpenseActual,
    }));
  }, [groupSummaries, visibleGroupIds, chartMetric]);

  // ---- Chart 4: Stacked Bar ----
  const stackedBarSeriesData = useMemo(() => {
    if (groupBy !== "custom" || selectedPropertyIds.length === 0)
      return { series: [], data: [] as number[][] };
    const selected = properties.filter((p) =>
      selectedPropertyIds.includes(p.id),
    );
    const series = selected.map((p, pi) => {
      const alpha = Math.round(255 * (0.3 + (0.7 * (pi + 1)) / selected.length))
        .toString(16)
        .padStart(2, "0");
      return {
        id: p.id,
        label: p.name,
        data: months12.map((_m, i) => {
          if (chartMetric === "noi")
            return (
              p.monthlyData[i].incomeActual - p.monthlyData[i].expenseActual
            );
          if (chartMetric === "income") return p.monthlyData[i].incomeActual;
          return p.monthlyData[i].expenseActual;
        }),
        color: `#0088aa${alpha}`,
        stack: "total",
      };
    });
    return { series };
  }, [groupBy, selectedPropertyIds, chartMetric]);

  // ---- Card Filtering ----
  const filteredSummaries = useMemo(() => {
    if (!cardSearchQuery.trim()) return groupSummaries;
    const q = cardSearchQuery.toLowerCase();
    return groupSummaries.filter((g) => g.name.toLowerCase().includes(q));
  }, [groupSummaries, cardSearchQuery]);

  const shouldCollapse = filteredSummaries.length > COLLAPSED_VISIBLE;
  const visibleSummaries =
    shouldCollapse && !cardsExpanded
      ? filteredSummaries.slice(0, COLLAPSED_VISIBLE)
      : filteredSummaries;
  const hiddenCount = filteredSummaries.length - COLLAPSED_VISIBLE;

  const toggleProperty = (id: string) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 6,
            height: 28,
            bgcolor: "primary.main",
            borderRadius: 1,
          }}
        />
        <Typography variant="h6" fontWeight="bold">
          予実分析（@mui/x-charts サンプル）
        </Typography>
      </Box>

      {/* Top Controls */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value as string)}
            >
              <MenuItem value="2024">2024年度</MenuItem>
              <MenuItem value="2025">2025年度</MenuItem>
              <MenuItem value="2026">2026年度</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            >
              <MenuItem value="project">プロジェクト別</MenuItem>
              <MenuItem value="am">AM会社別</MenuItem>
              <MenuItem value="custom">物件を選択</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={chartMetric}
              onChange={(e) => setChartMetric(e.target.value as ChartMetric)}
            >
              <MenuItem value="noi">NOI</MenuItem>
              <MenuItem value="income">収入</MenuItem>
              <MenuItem value="expense">支出</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
          >
            CSV出力
          </Button>
        </Stack>
      </Paper>

      {/* Custom Property Selector */}
      {groupBy === "custom" && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">物件選択</Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value as string)}
              >
                <MenuItem value="all">全プロジェクト</MenuItem>
                {projects.map((pj) => (
                  <MenuItem key={pj.id} value={pj.id}>
                    {pj.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={filterAmId}
                onChange={(e) => setFilterAmId(e.target.value as string)}
              >
                <MenuItem value="all">全AM会社</MenuItem>
                {ams.map((am) => (
                  <MenuItem key={am.id} value={am.id}>
                    {am.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setSelectedPropertyIds(filteredProperties.map((p) => p.id))
              }
            >
              全選択
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedPropertyIds([])}
            >
              全解除
            </Button>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
              gap: 1,
            }}
          >
            {filteredProperties.map((p) => {
              const pj = projects.find((x) => x.id === p.projectId);
              const am = ams.find((x) => x.id === p.amId);
              const checked = selectedPropertyIds.includes(p.id);
              return (
                <Paper
                  key={p.id}
                  variant="outlined"
                  onClick={() => toggleProperty(p.id)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderColor: checked ? "primary.main" : "divider",
                    bgcolor: checked ? "primary.50" : "background.paper",
                    "&:hover": { borderColor: "primary.light" },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ApartmentIcon
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {p.name}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} mt={0.5}>
                        <Chip
                          label={pj?.name}
                          size="small"
                          sx={{ fontSize: 10, height: 18 }}
                        />
                        <Chip
                          label={am?.name}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 18 }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
          {selectedPropertyIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" mt={1.5}>
              {selectedPropertyIds.length}件の物件を選択中
            </Typography>
          )}
        </Paper>
      )}

      {/* ===== 1) Portfolio Overview (AreaChart) ===== */}
      {groupBy !== "custom" && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
            {/* Left: KPI */}
            <Box sx={{ width: { lg: 280 }, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                ポートフォリオ全体
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                累計サマリー
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "primary.50",
                    border: "1px solid",
                    borderColor: "primary.100",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    NOI実績
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" mt={0.5}>
                    {fmt(portfolioTotals.noiActual)}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <Typography variant="caption" color="text.secondary">
                      予算: {fmt(portfolioTotals.noiBudget)}
                    </Typography>
                    {portfolioTotals.noiActual - portfolioTotals.noiBudget >=
                    0 ? (
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <TrendingUpIcon
                          sx={{ fontSize: 14, color: "success.main" }}
                        />
                        <Typography
                          variant="caption"
                          color="success.main"
                          fontWeight={500}
                        >
                          +
                          {fmtShort(
                            portfolioTotals.noiActual -
                              portfolioTotals.noiBudget,
                          )}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <TrendingDownIcon
                          sx={{ fontSize: 14, color: "error.main" }}
                        />
                        <Typography
                          variant="caption"
                          color="error.main"
                          fontWeight={500}
                        >
                          {fmtShort(
                            portfolioTotals.noiActual -
                              portfolioTotals.noiBudget,
                          )}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
                <Stack direction="row" spacing={1.5} mt={1.5}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, flex: 1, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      収入実績
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" mt={0.5}>
                      {fmtShort(portfolioTotals.incomeActual)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      達成率{" "}
                      {portfolioTotals.incomeBudget > 0
                        ? (
                            (portfolioTotals.incomeActual /
                              portfolioTotals.incomeBudget) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, flex: 1, borderRadius: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      支出実績
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" mt={0.5}>
                      {fmtShort(portfolioTotals.expenseActual)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      執行率{" "}
                      {portfolioTotals.expenseBudget > 0
                        ? (
                            (portfolioTotals.expenseActual /
                              portfolioTotals.expenseBudget) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                  </Paper>
                </Stack>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, mt: 1.5, borderRadius: 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    物件数 / グループ数
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" mt={0.5}>
                    {properties.length}物件 / {groups.length}グループ
                  </Typography>
                </Paper>
              </Box>
            </Box>

            {/* Right: Area Chart (using LineChart with area) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {metricLabel} 全体推移
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  全物件合計の予算 vs 実績
                </Typography>
              </Stack>
              <Box sx={{ height: 260 }}>
                <LineChart
                  xAxis={[{ data: months12, scaleType: "point" }]}
                  yAxis={[
                    {
                      valueFormatter: (v: number | null) => fmtShort(v ?? 0),
                    },
                  ]}
                  series={[
                    {
                      data: portfolioChartData.budget,
                      label: "予算",
                      color: "#00a0c8",
                      area: true,
                      showMark: false,
                    },
                    {
                      data: portfolioChartData.actual,
                      label: "実績",
                      color: "#0077a3",
                      area: true,
                      showMark: true,
                    },
                  ]}
                  height={260}
                  sx={{
                    "& .MuiLineElement-series-auto-generated-id-0": {
                      strokeDasharray: "6 3",
                      strokeWidth: 1.5,
                    },
                    "& .MuiLineElement-series-auto-generated-id-1": {
                      strokeWidth: 2.5,
                    },
                    "& .MuiAreaElement-series-auto-generated-id-0": {
                      fillOpacity: 0.08,
                    },
                    "& .MuiAreaElement-series-auto-generated-id-1": {
                      fillOpacity: 0.2,
                    },
                  }}
                  slotProps={{
                    legend: { labelStyle: { fontSize: 11 } },
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ===== 2) KPI Summary Cards ===== */}
      {groupSummaries.length > 0 && (
        <Box>
          {groupSummaries.length > 3 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              mb={2}
            >
              <TextField
                size="small"
                placeholder="グループ名で検索..."
                value={cardSearchQuery}
                onChange={(e) => {
                  setCardSearchQuery(e.target.value);
                  setCardsExpanded(true);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 300 }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {filteredSummaries.length}件
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={cardLayout}
                exclusive
                onChange={(_e, val) => val && setCardLayout(val)}
              >
                <ToggleButton value="grid">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          {/* Cards */}
          <Box
            sx={{
              display: cardLayout === "grid" ? "grid" : "flex",
              gridTemplateColumns:
                cardLayout === "grid"
                  ? { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" }
                  : undefined,
              flexDirection: cardLayout === "list" ? "column" : undefined,
              gap: 2,
            }}
          >
            {visibleSummaries.map((g) => (
              <SummaryCard
                key={g.id}
                summary={g}
                chartMetric={chartMetric}
                isVisibleInChart={visibleGroupIds.has(g.id)}
                onToggleVisibility={() => toggleGroupVisibility(g.id)}
                seriesStyle={getSeriesStyle(g.id)}
                layout={cardLayout}
              />
            ))}
          </Box>

          {shouldCollapse && !cardSearchQuery.trim() && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                size="small"
                onClick={() => setCardsExpanded(!cardsExpanded)}
                startIcon={
                  cardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
              >
                {cardsExpanded ? "折りたたむ" : `他${hiddenCount}件を表示`}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ===== 3) Comparison Charts (project/am mode) ===== */}
      {groupSummaries.length > 0 && groupBy !== "custom" && (
        <Box>
          {/* Group selector */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
              mb={1.5}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                グループ比較チャート
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {visibleGroupIds.size}/{groups.length}件表示中
              </Typography>
              <Button
                size="small"
                onClick={() =>
                  setVisibleGroupIds(new Set(groups.map((g) => g.id)))
                }
              >
                全選択
              </Button>
              <Button
                size="small"
                onClick={() => setVisibleGroupIds(new Set())}
              >
                全解除
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {groups.map((g) => {
                const isOn = visibleGroupIds.has(g.id);
                const style = getSeriesStyle(g.id);
                return (
                  <Chip
                    key={g.id}
                    label={g.name}
                    size="small"
                    onClick={() => toggleGroupVisibility(g.id)}
                    icon={
                      isOn ? (
                        <svg width={20} height={10}>
                          <line
                            x1="0"
                            y1="5"
                            x2="20"
                            y2="5"
                            stroke={style.stroke}
                            strokeWidth="2.5"
                            strokeDasharray={style.dash || undefined}
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        <VisibilityOffIcon sx={{ fontSize: 14 }} />
                      )
                    }
                    variant={isOn ? "filled" : "outlined"}
                    color={isOn ? "primary" : "default"}
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
            </Stack>
          </Paper>

          {visibleGroupIds.size > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* LineChart: monthly comparison */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {metricLabel} 月次推移比較
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={2}
                  display="block"
                >
                  選択グループの予算(破線) vs 実績(実線)
                </Typography>
                <Box sx={{ height: 300 }}>
                  <LineChart
                    xAxis={[{ data: months12, scaleType: "point" }]}
                    yAxis={[
                      {
                        valueFormatter: (v: number | null) => fmtShort(v ?? 0),
                      },
                    ]}
                    series={comparisonSeriesData.map((s) => ({
                      data: s.data,
                      label: s.label,
                      color: s.color,
                      showMark: !s.dashStyle,
                    }))}
                    height={300}
                    margin={{ top: 20 }}
                    sx={{
                      ...Object.fromEntries(
                        comparisonSeriesData
                          .filter((s) => s.dashStyle)
                          .map((s) => {
                            const seriesIdx = comparisonSeriesData.indexOf(s);
                            return [
                              `& .MuiLineElement-series-auto-generated-id-${seriesIdx}`,
                              {
                                strokeDasharray: s.dashStyle,
                                strokeWidth: 1.5,
                                strokeOpacity: 0.5,
                              },
                            ];
                          }),
                      ),
                    }}
                    slotProps={{
                      legend: { hidden: true },
                    }}
                  />
                </Box>
              </Paper>

              {/* BarChart: YTD horizontal comparison */}
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {metricLabel} 累計比較
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={2}
                  display="block"
                >
                  選択グループの{fiscalYear}年度累計 予算 vs 実績
                </Typography>
                <Box sx={{ height: 300 }}>
                  <BarChart
                    yAxis={[
                      {
                        data: ytdBarData.map((d) => d.name),
                        scaleType: "band",
                      },
                    ]}
                    xAxis={[
                      {
                        valueFormatter: (v: number | null) => fmtShort(v ?? 0),
                      },
                    ]}
                    series={[
                      {
                        data: ytdBarData.map((d) => d.budget),
                        label: "予算",
                        color: BAR_BUDGET_COLOR,
                      },
                      {
                        data: ytdBarData.map((d) => d.actual),
                        label: "実績",
                        color: BAR_ACTUAL_COLOR,
                      },
                    ]}
                    layout="horizontal"
                    height={300}
                    slotProps={{
                      legend: { labelStyle: { fontSize: 11 } },
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          {visibleGroupIds.size === 0 && (
            <Paper variant="outlined" sx={{ p: 8, textAlign: "center" }}>
              <VisibilityIcon
                sx={{ fontSize: 32, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                上のトグルからグループを選択するとチャートが表示されます
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Custom stacked chart */}
      {groupBy === "custom" && selectedPropertyIds.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
            gap: 2,
          }}
        >
          {/* Line chart for custom */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {metricLabel} 予実推移
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={2}
              display="block"
            >
              選択物件合計の月次推移
            </Typography>
            <Box sx={{ height: 300 }}>
              <LineChart
                xAxis={[{ data: months12, scaleType: "point" }]}
                yAxis={[
                  { valueFormatter: (v: number | null) => fmtShort(v ?? 0) },
                ]}
                series={comparisonSeriesData.map((s) => ({
                  data: s.data,
                  label: s.label,
                  color: s.color,
                  showMark: !s.dashStyle,
                }))}
                height={300}
                slotProps={{
                  legend: { labelStyle: { fontSize: 11 } },
                }}
              />
            </Box>
          </Paper>

          {/* Stacked BarChart */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {metricLabel} 物件別積み上げ
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={2}
              display="block"
            >
              選択物件ごとの構成比を月別に確認
            </Typography>
            <Box sx={{ height: 300 }}>
              <BarChart
                xAxis={[{ data: months12, scaleType: "band" }]}
                yAxis={[
                  { valueFormatter: (v: number | null) => fmtShort(v ?? 0) },
                ]}
                series={stackedBarSeriesData.series.map((s) => ({
                  data: s.data,
                  label: s.label,
                  color: s.color,
                  stack: s.stack,
                }))}
                height={300}
                slotProps={{
                  legend: { labelStyle: { fontSize: 11 } },
                }}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {groups.length === 0 && groupBy === "custom" && (
        <Paper variant="outlined" sx={{ p: 8, textAlign: "center" }}>
          <ApartmentIcon
            sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
          />
          <Typography variant="body2" color="text.secondary">
            物件を選択すると予実分析が表示されます
          </Typography>
        </Paper>
      )}

      {/* ===== 4) Detail Table ===== */}
      {groupSummaries.length > 0 && (
        <Paper variant="outlined">
          <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              グループ別集計
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 500, minWidth: 180 }}>
                    グループ名
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "center", minWidth: 60 }}
                  >
                    物件数
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    収入予算
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    収入実績
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 80 }}
                  >
                    達成率
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    支出予算
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    支出実績
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    NOI予算
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 120 }}
                  >
                    NOI実績
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, textAlign: "right", minWidth: 100 }}
                  >
                    NOI差異
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupSummaries.map((g) => {
                  const incomeRate =
                    g.ytdIncomeBudget > 0
                      ? (g.ytdIncomeActual / g.ytdIncomeBudget) * 100
                      : 0;
                  const noiDiff = g.ytdNOIActual - g.ytdNOIBudget;
                  return (
                    <TableRow key={g.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{g.name}</TableCell>
                      <TableCell
                        sx={{ textAlign: "center", color: "text.secondary" }}
                      >
                        {g.propertyCount}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdIncomeBudget)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: 500,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdIncomeActual)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: 500,
                          color:
                            incomeRate >= 100 ? "success.main" : "error.main",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {incomeRate.toFixed(1)}%
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdExpenseBudget)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: 500,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdExpenseActual)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdNOIBudget)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(g.ytdNOIActual)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: 500,
                          color: noiDiff >= 0 ? "success.main" : "error.main",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {noiDiff >= 0 ? "+" : ""}
                        {fmt(noiDiff)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {groupSummaries.length > 1 && (
                  <TableRow
                    sx={{
                      borderTop: 2,
                      borderColor: "divider",
                      bgcolor: "action.hover",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>合計</TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: 500 }}>
                      {groupSummaries.reduce((s, g) => s + g.propertyCount, 0)}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce(
                          (s, g) => s + g.ytdIncomeBudget,
                          0,
                        ),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce(
                          (s, g) => s + g.ytdIncomeActual,
                          0,
                        ),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {(() => {
                        const b = groupSummaries.reduce(
                          (s, g) => s + g.ytdIncomeBudget,
                          0,
                        );
                        const a = groupSummaries.reduce(
                          (s, g) => s + g.ytdIncomeActual,
                          0,
                        );
                        return b > 0 ? `${((a / b) * 100).toFixed(1)}%` : "-";
                      })()}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce(
                          (s, g) => s + g.ytdExpenseBudget,
                          0,
                        ),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce(
                          (s, g) => s + g.ytdExpenseActual,
                          0,
                        ),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce((s, g) => s + g.ytdNOIBudget, 0),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: "primary.main",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmt(
                        groupSummaries.reduce((s, g) => s + g.ytdNOIActual, 0),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 700,
                        color:
                          groupSummaries.reduce(
                            (s, g) => s + g.ytdNOIActual,
                            0,
                          ) -
                            groupSummaries.reduce(
                              (s, g) => s + g.ytdNOIBudget,
                              0,
                            ) >=
                          0
                            ? "success.main"
                            : "error.main",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {(() => {
                        const d =
                          groupSummaries.reduce(
                            (s, g) => s + g.ytdNOIActual,
                            0,
                          ) -
                          groupSummaries.reduce(
                            (s, g) => s + g.ytdNOIBudget,
                            0,
                          );
                        return `${d >= 0 ? "+" : ""}${fmt(d)}`;
                      })()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

// ---- Summary Card Sub-component ----
interface GroupSummaryType {
  id: string;
  name: string;
  properties: Property[];
  propertyCount: number;
  agg: ReturnType<typeof aggregateMonthly>;
  ytdIncomeBudget: number;
  ytdIncomeActual: number;
  ytdExpenseBudget: number;
  ytdExpenseActual: number;
  ytdNOIBudget: number;
  ytdNOIActual: number;
}

function SummaryCard({
  summary: g,
  chartMetric,
  isVisibleInChart,
  onToggleVisibility,
  seriesStyle,
  layout,
}: {
  summary: GroupSummaryType;
  chartMetric: ChartMetric;
  isVisibleInChart: boolean;
  onToggleVisibility: () => void;
  seriesStyle: { stroke: string; dash: string };
  layout: CardLayout;
}) {
  const [expanded, setExpanded] = useState(false);
  const incomeRate =
    g.ytdIncomeBudget > 0 ? (g.ytdIncomeActual / g.ytdIncomeBudget) * 100 : 0;
  const expenseRate =
    g.ytdExpenseBudget > 0
      ? (g.ytdExpenseActual / g.ytdExpenseBudget) * 100
      : 0;
  const noiDiff = g.ytdNOIActual - g.ytdNOIBudget;

  const sparkData = g.agg.map((m) =>
    chartMetric === "noi"
      ? m.noiActual
      : chartMetric === "income"
        ? m.incomeActual
        : m.expenseActual,
  );

  if (layout === "list") {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: isVisibleInChart ? "primary.main" : "divider",
          ...(isVisibleInChart && {
            boxShadow: "0 0 0 1px rgba(0,119,163,0.2)",
          }),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 180 }}
          >
            <svg width={16} height={10}>
              <line
                x1="0"
                y1="5"
                x2="16"
                y2="5"
                stroke={seriesStyle.stroke}
                strokeWidth="2.5"
                strokeDasharray={seriesStyle.dash || undefined}
                strokeLinecap="round"
              />
            </svg>
            <Typography variant="body2" fontWeight={600} noWrap>
              {g.name}
            </Typography>
            <Chip
              label={`${g.propertyCount}物件`}
              size="small"
              sx={{ fontSize: 10, height: 18 }}
            />
          </Stack>
          <MiniSparkline data={sparkData} style={seriesStyle} />
          <Stack direction="row" spacing={3} flex={1} flexWrap="wrap">
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                NOI実績
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {fmt(g.ytdNOIActual)}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 100 }}>
              <Typography variant="caption" color="text.secondary">
                NOI差異
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={noiDiff >= 0 ? "success.main" : "error.main"}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {noiDiff >= 0 ? "+" : ""}
                {fmt(noiDiff)}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 100 }}>
              <Typography variant="caption" color="text.secondary">
                収入達成率
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={Math.min(incomeRate, 100)}
                  sx={{ width: 64, height: 6, borderRadius: 1 }}
                />
                <Typography
                  variant="caption"
                  fontWeight={500}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {incomeRate.toFixed(1)}%
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpanded(!expanded)}
              startIcon={<ApartmentIcon sx={{ fontSize: 14 }} />}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              内訳
            </Button>
            <IconButton
              size="small"
              onClick={onToggleVisibility}
              color={isVisibleInChart ? "primary" : "default"}
            >
              {isVisibleInChart ? (
                <VisibilityIcon fontSize="small" />
              ) : (
                <VisibilityOffIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        </Stack>
        {expanded && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <PropertyBreakdown groupProperties={g.properties} />
          </Box>
        )}
      </Paper>
    );
  }

  // Grid card
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderColor: isVisibleInChart ? "primary.main" : "divider",
        ...(isVisibleInChart && { boxShadow: "0 0 0 1px rgba(0,119,163,0.2)" }),
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <svg width={16} height={10}>
            <line
              x1="0"
              y1="5"
              x2="16"
              y2="5"
              stroke={seriesStyle.stroke}
              strokeWidth="2.5"
              strokeDasharray={seriesStyle.dash || undefined}
              strokeLinecap="round"
            />
          </svg>
          <Typography variant="body2" fontWeight={600} noWrap>
            {g.name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            label={`${g.propertyCount}物件`}
            size="small"
            sx={{ fontSize: 10, height: 18 }}
          />
          <IconButton
            size="small"
            onClick={onToggleVisibility}
            color={isVisibleInChart ? "primary" : "default"}
          >
            {isVisibleInChart ? (
              <VisibilityIcon sx={{ fontSize: 16 }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {/* NOI + Sparkline */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              NOI (累計)
            </Typography>
            {noiDiff >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 14, color: "success.main" }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 14, color: "error.main" }} />
            )}
          </Stack>
          <Typography variant="h6" fontWeight="bold" mt={0.5}>
            {fmt(g.ytdNOIActual)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Typography variant="caption" color="text.secondary">
              予算: {fmt(g.ytdNOIBudget)}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={500}
              color={noiDiff >= 0 ? "success.main" : "error.main"}
            >
              {noiDiff >= 0 ? "+" : ""}
              {fmt(noiDiff)}
            </Typography>
          </Stack>
        </Box>
        <MiniSparkline data={sparkData} style={seriesStyle} />
      </Stack>

      {/* Income / Expense */}
      <Stack
        direction="row"
        spacing={2}
        mt={2}
        pt={2}
        sx={{ borderTop: 1, borderColor: "divider" }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" mb={0.5}>
            収入
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {fmtShort(g.ytdIncomeActual)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(incomeRate, 100)}
            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {incomeRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" mb={0.5}>
            支出
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {fmtShort(g.ytdExpenseActual)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(expenseRate, 100)}
            color={expenseRate > 100 ? "error" : "success"}
            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {expenseRate.toFixed(1)}%
          </Typography>
        </Box>
      </Stack>

      {/* Property breakdown */}
      <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          startIcon={
            <ChevronRightIcon
              sx={{
                transform: expanded ? "rotate(90deg)" : "none",
                transition: "0.2s",
              }}
            />
          }
          sx={{ fontSize: 12, color: "text.secondary" }}
        >
          <ApartmentIcon sx={{ fontSize: 14, mr: 0.5 }} />
          物件内訳を{expanded ? "閉じる" : "表示"}
        </Button>
        {expanded && (
          <Box mt={1}>
            <PropertyBreakdown groupProperties={g.properties} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function PropertyBreakdown({
  groupProperties,
}: {
  groupProperties: Property[];
}) {
  const sorted = [...groupProperties].sort(
    (a, b) => propertyNOI(b) - propertyNOI(a),
  );
  return (
    <Stack spacing={0.5}>
      {sorted.map((p) => {
        const noi = propertyNOI(p);
        const pj = projects.find((x) => x.id === p.projectId);
        return (
          <Stack
            key={p.id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <ApartmentIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" fontWeight={500} noWrap>
                  {p.name}
                </Typography>
                {pj && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {pj.name}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color={noi >= 0 ? "text.primary" : "error.main"}
              sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
            >
              {fmtShort(noi)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default BudgetAnalysisSample;
